// The bridge's calls and their replies, as the page answers them. A call is
// `{ type: 'call', id, kind: 'command', name, args }`, one of the named
// commands (`commands.ts`), or, on the dev server only, `{ type: 'call', id,
// kind: 'eval', expression }`, evaluated with `registry` and `host` in
// scope; either answers `{ type: 'reply', id, ok: true, result }` or
// `{ type: 'reply', id, ok: false, error }`. Anything else on the wire is
// ignored. Pure: the registry and the page come in as functions, so this
// runs under Node's test runner.
import { runCommand, type CommandArgs, type CommandContext } from './commands.ts';
import { serialize, type SerializeLimits } from './serialize.ts';

export interface EvalCall {
  type: 'call';
  id: string;
  kind: 'eval';
  expression: string;
}

export interface CommandCall {
  type: 'call';
  id: string;
  kind: 'command';
  name: string;
  args: CommandArgs;
}

export type Call = EvalCall | CommandCall;

export type Reply = { type: 'reply'; id: string; ok: true; result: unknown } | { type: 'reply'; id: string; ok: false; error: string };

/** The page's side of a call: what the expression sees, whether it may run at all, and what the commands need. */
export interface CallContext {
  /** Resolves the registry and the host for an expression; undefined before the page has exposed them. */
  scope(): { registry: unknown; host: unknown } | undefined;
  /** True on the dev server only (`import.meta.env.DEV`); elsewhere an eval call is refused. */
  evalAllowed: boolean;
  commands: CommandContext;
  limits?: SerializeLimits;
}

export const EVAL_UNAVAILABLE = 'page_eval is only available on the dev server';

/** The call in a frame's text, or undefined for bad JSON, another type, or a malformed call. */
export function parseCall(text: unknown): Call | undefined {
  if (typeof text !== 'string') return undefined;
  let message: unknown;
  try {
    message = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (!message || typeof message !== 'object') return undefined;
  const m = message as Record<string, unknown>;
  if (m.type !== 'call' || typeof m.id !== 'string') return undefined;
  if (m.kind === 'eval' && typeof m.expression === 'string') {
    return { type: 'call', id: m.id, kind: 'eval', expression: m.expression };
  }
  if (m.kind === 'command' && typeof m.name === 'string') {
    const args = m.args === undefined || m.args === null ? {} : typeof m.args === 'object' && !Array.isArray(m.args) ? (m.args as CommandArgs) : undefined;
    if (!args) return undefined;
    return { type: 'call', id: m.id, kind: 'command', name: m.name, args };
  }
  return undefined;
}

/** Answers a call; never throws: a failure is a reply with `ok: false`. */
export async function handleCall(call: Call, context: CallContext): Promise<Reply> {
  try {
    const result = call.kind === 'eval' ? await evaluate(call.expression, context) : await runCommand(call.name, call.args, context.commands);
    return { type: 'reply', id: call.id, ok: true, result: serialize(result, context.limits) };
  } catch (error) {
    return { type: 'reply', id: call.id, ok: false, error: errorMessage(error) };
  }
}

/** `expression` with `registry` and `host` in scope; a thenable result is awaited. Dev server only. */
export async function evaluate(expression: string, context: CallContext): Promise<unknown> {
  if (!context.evalAllowed) throw new Error(EVAL_UNAVAILABLE);
  const scope = context.scope();
  if (!scope) throw new Error('the page has not exposed window.__host yet');
  const fn = new Function('registry', 'host', `return (${expression}\n);`) as (registry: unknown, host: unknown) => unknown;
  return await fn(scope.registry, scope.host);
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}
